import { useExpertsData } from "@/data/use-experts-data";
import { useGroupExpertsData } from "@/data/use-group-experts-data";
import { useKitData } from "@/data/use-kits-data";
import { useSettingData } from "@/data/use-settings-data";
import { Expert } from "@/types/types";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { trimMax } from "./lib";

export const MAX_EXPERTS_IN_CHAT = 3;

type ExpertSelectionContextProps = {
  kitExperts: Expert[];
  groupExperts: Expert[];
  availableExperts: Expert[];
  selectedExperts: Expert[];
  isLoading: boolean;
  selectedExpertUuids: Set<string>;
  selectExpert: (uuid: string) => void;
  unselectExpert: (uuid: string) => void;
  selectExperts: (uuids: Set<string>) => void;
  linkExperts: (uuids: Set<string>) => Promise<void>;
  unlinkExperts: (uuids: Set<string>) => void;
  max: number;
  enableInviteDialog: boolean;
  groupUuid: string;
  disableSelection?: boolean;
  kitType?: string;
};

const ExpertSelectionContext = createContext<ExpertSelectionContextProps>({
  kitExperts: [],
  groupExperts: [],
  availableExperts: [],
  selectedExperts: [],
  isLoading: false,
  selectedExpertUuids: new Set(),
  selectExpert: () => {},
  unselectExpert: () => {},
  selectExperts: () => {},
  linkExperts: async () => {},
  unlinkExperts: () => {},
  max: 1,
  enableInviteDialog: false,
  groupUuid: "",
});

type WithExpertSelectionProps = {
  groupUuid: string;
  kitUuid: string | null;
};

export const WithExpertSelection: React.FC<
  React.PropsWithChildren<WithExpertSelectionProps>
> = ({ children, groupUuid, kitUuid }) => {
  const { kit, linkExpert, unlinkExpert } = useKitData(groupUuid, kitUuid);
  const { value: multiple } = useSettingData(
    groupUuid,
    "FEATURE_FLAG_MULTIPLE_EXPERTS_CHAT",
  );
  const { experts: groupExpertsData, isLoading: isLoadingGroupExperts } =
    useGroupExpertsData(groupUuid);
  const { experts: kitExpertsData, isLoading: isLoadingKitExperts } =
    useExpertsData(groupUuid, kitUuid);
  const [selectedExpertUuids, setSelectedExpertUuids] = useState<Set<string>>(
    new Set(),
  );
  const previousKitUuid = useRef<string | null>(null);

  const groupExperts = useMemo(
    () =>
      groupExpertsData.filter((expert) => expert.expertType !== "interviewer"),
    [groupExpertsData],
  );
  const kitExperts = useMemo(
    () =>
      kitExpertsData.filter((expert) =>
        kit?.type === "interview" ? true : expert.expertType !== "interviewer",
      ),
    [kitExpertsData, kit],
  );
  const availableExperts =
    kit === undefined
      ? groupExperts
      : [
          ...kitExperts,
          ...groupExperts.filter(
            (we) => !kitExperts.find((ke) => ke.uuid === we.uuid),
          ),
        ].sort((a, b) => a.name.localeCompare(b.name));

  const selectedExperts = availableExperts.filter((expert) =>
    selectedExpertUuids.has(expert.uuid),
  );

  const max = multiple === "enabled" ? MAX_EXPERTS_IN_CHAT : 1;

  // Clear selection when group changes
  useEffect(() => {
    setSelectedExpertUuids(new Set());
  }, [groupUuid]);

  const _linkExpertIfNeeded = useCallback(
    (expertUuid: string) => {
      if (
        kit?.type === "conversation" &&
        !kitExperts.find((expert) => expert.uuid === expertUuid)
      ) {
        void linkExpert({
          expertUuid: expertUuid,
        }).catch((error: unknown) => {
          console.error(error);
        });
      }
    },
    [kit?.type, kitExperts, linkExpert],
  );

  const selectExpert = (uuid: string) => {
    if (!selectedExpertUuids.has(uuid)) {
      const updatedSelected = trimMax(
        new Set(selectedExpertUuids).add(uuid),
        max,
      );
      setSelectedExpertUuids(updatedSelected);
    }

    _linkExpertIfNeeded(uuid);
  };

  const unselectExpert = (uuid: string) => {
    setSelectedExpertUuids((prev) => {
      const newSet = new Set(prev);
      newSet.delete(uuid);
      return newSet;
    });
    // Remove from kit if needed
    if (
      kit?.type === "conversation" &&
      kitExperts.find((ek) => ek.uuid === uuid)
    ) {
      unlinkExpert({
        expertUuid: uuid,
      });
    }
  };

  const selectExperts = useCallback(
    (uuids: Set<string>) => {
      const trimmed = trimMax(uuids, max);
      const currentSignature = Array.from(selectedExpertUuids).sort().join(",");
      const newSignature = Array.from(trimmed).sort().join(",");
      if (currentSignature === newSignature) {
        return;
      }

      setSelectedExpertUuids(trimmed);

      for (const uuid of trimmed) {
        _linkExpertIfNeeded(uuid);
      }
    },
    [max, selectedExpertUuids, _linkExpertIfNeeded],
  );

  useEffect(() => {
    if (isLoadingKitExperts) {
      return;
    }

    if (previousKitUuid.current === kitUuid) {
      return;
    }
    previousKitUuid.current = kitUuid;

    if (kitExperts.length === 0) {
      setSelectedExpertUuids(new Set());
      return;
    }
    selectExperts(new Set([kitExperts[0].uuid]));
  }, [isLoadingKitExperts, kitExperts, kitUuid, selectExperts]);

  const linkExperts = async (uuids: Set<string>) => {
    await Promise.all(
      [...uuids].map((expertUuid) => linkExpert({ expertUuid })),
    );
  };

  const unlinkExperts = (uuids: Set<string>) => {
    for (const uuid of uuids) {
      unlinkExpert({
        expertUuid: uuid,
      });
    }
  };

  return (
    <ExpertSelectionContext.Provider
      value={{
        kitExperts,
        groupExperts,
        availableExperts,
        selectedExperts,
        isLoading: isLoadingGroupExperts || isLoadingKitExperts,
        selectedExpertUuids,
        selectExpert: selectExpert,
        unselectExpert: unselectExpert,
        selectExperts: selectExperts,
        linkExperts: linkExperts,
        unlinkExperts: unlinkExperts,
        max,
        enableInviteDialog: kit !== undefined && kit.type !== "conversation",
        disableSelection: kit?.type === "interview",
        groupUuid,
        kitType: kit?.type,
      }}
    >
      {children}
    </ExpertSelectionContext.Provider>
  );
};

export const useExpertSelection = () => {
  return useContext(ExpertSelectionContext);
};
